/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import * as queryString from 'query-string';
import { H2, Label, PageActions, PageHeader, PageHeaderContent, PageLabel, TabNav, TabNavItem } from 'components';
// importing this from 'components' is causing a circular dependency
import ConnectedSiteSelector from 'components/SiteSelector/ConnectedSiteSelector';
import { SiteSelectorAppearance } from 'components/SiteSelector/types';
// relative path doesn't import
import { theme } from 'design';
import ListContext, { FetchDataQueryType, ListContextProvider } from 'providers/ListContextProvider';
import { routeConstants } from 'routes';
import { routeForPaymentDetail } from 'routes/routeConstants';
import { TipAcceptancePayments } from 'screens';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { getSitePaymentsRequest, Payments, selectPaymentSetsForSite, selectTotalCountPaymentSetsForSite } from 'store/Collections/Payments';
import { getSiteReviewPayoutsCountRequest } from 'store/Collections/Payouts';
import { selectCurrentEmployeeOrg, selectCurrentEmployeeSite } from 'store/Employers';
import { getSiteBalanceRequest, selectSiteAcceptance, selectSiteAcceptanceBalance, selectSiteAccountBalance } from 'store/Site';
import {
  getAcceptancePaymentsPendingCountRequest,
  getSitePayoutStatsRequest,
  selectPayoutStatsByDate,
  selectPayoutStatsNetTotal,
  selectPayoutStatsStatusCount,
  selectReviewPayoutsCount,
  selectTipAcceptancePendingPaymentsCount,
  Stats,
} from 'store/Stats';
import { IStore } from 'store/types';
import { ActivePaymentsTab, ApiQueryOperator, DisbursementMethod, IEmployerSite, PayoutStatus, PermissionType } from 'types';
import { sumBy } from 'utils/arrays';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateForApi } from 'utils/date';
import { hasSitePermission } from 'utils/employers';
import BusinessDaySection from './BusinessDaySection';
import { DetailsTab } from './DetailsTab';
import { OverviewTab } from './OverviewTab';
import ReviewPayments from './ReviewPayments';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT = '-payment_date';
const DEFAULT_END_DATE = formatDateForApi(dayjs(new Date()).format());
const DEFAULT_START_DATE = formatDateForApi(dayjs(new Date()).subtract(6, 'day').format());

const PaymentsScreen = ({ activeTab }: { activeTab: ActivePaymentsTab }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { data } = queryString.parse(location.search);
  const currentOrganization = useSelector(selectCurrentEmployeeOrg);
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const history = useHistory();
  const currentSiteAcceptance = useSelector(selectSiteAcceptance());
  const [sharedDates, setSharedDates] = useState<string[] | undefined>([DEFAULT_START_DATE, DEFAULT_END_DATE]);
  const acceptancePendingTotal = useSelector(selectTipAcceptancePendingPaymentsCount()) || 0;
  const reviewTotal = useSelector(selectReviewPayoutsCount()) || 0;
  const reviewPermission = hasSitePermission(currentSite?.permissions, PermissionType.PAYMENT_APPROVAL);

  const currentSiteId = useSelector(selectCurrentEmployee())?.siteId;

  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payments.GET_SITE_PAYMENTS_REQUEST));
  const isStatsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Stats.GET_SITE_PAYOUT_STATS_REQUEST));

  const totalCountPaymentSetsSelector = selectTotalCountPaymentSetsForSite();
  const paymentSets = useSelector(selectPaymentSetsForSite());

  // payments selector
  const paymentStatsByDate = useSelector(selectPayoutStatsByDate());

  const grossTotal = paymentStatsByDate ? sumBy(paymentStatsByDate, 'gross') : 0;
  const netTotal = paymentStatsByDate ? sumBy(paymentStatsByDate, 'net') : 0;
  const holdbackTotal = paymentStatsByDate ? sumBy(paymentStatsByDate, 'holdbacks') : 0;
  const instantTotal = paymentStatsByDate ? sumBy(paymentStatsByDate, DisbursementMethod.INSTANT) : 0;
  const payrollTotal = paymentStatsByDate ? sumBy(paymentStatsByDate, DisbursementMethod.PAYROLL) : 0;

  const paidCount = useSelector(selectPayoutStatsStatusCount(PayoutStatus.PAID));
  const pendingCount = useSelector(selectPayoutStatsStatusCount(PayoutStatus.PENDING));
  const expiredCount = useSelector(selectPayoutStatsStatusCount(PayoutStatus.EXPIRED));
  const canceledCount = useSelector(selectPayoutStatsStatusCount(PayoutStatus.CANCELED));

  const paidTotal = useSelector(selectPayoutStatsNetTotal(PayoutStatus.PAID));
  const pendingTotal = useSelector(selectPayoutStatsNetTotal(PayoutStatus.PENDING));
  const expiredTotal = useSelector(selectPayoutStatsNetTotal(PayoutStatus.EXPIRED));
  const canceledTotal = useSelector(selectPayoutStatsNetTotal(PayoutStatus.CANCELED));

  const navigateToPaymentDetails = (paymentDetailId: string) => {
    history.push(routeForPaymentDetail(paymentDetailId));
  };

  useEffect(() => {
    if (currentSite) dispatch(getSiteBalanceRequest(currentSite.id));
  }, [dispatch, currentSite]);

  useEffect(() => {
    if (currentSiteId && currentSiteAcceptance?.enabled) {
      dispatch(getAcceptancePaymentsPendingCountRequest(currentSiteId));
    }
  }, [dispatch, currentSiteId, currentSiteAcceptance]);

  useEffect(() => {
    if (currentSiteId && reviewPermission) {
      dispatch(getSiteReviewPayoutsCountRequest(currentSiteId));
    }
  }, [dispatch, currentSiteId, reviewPermission]);

  useEffect(() => {
    if (activeTab === ActivePaymentsTab.OVERVIEW && currentSite && sharedDates?.[0] && sharedDates?.[1]) {
      dispatch(getSitePayoutStatsRequest(currentSite.id, formatDateForApi(sharedDates[0]), formatDateForApi(sharedDates[1])));
    }
  }, [dispatch, currentSite?.id, activeTab, sharedDates]);

  useEffect(() => {
    if (activeTab === ActivePaymentsTab.ACCEPTANCE && (!currentSiteId || (currentSiteAcceptance && !currentSiteAcceptance.enabled))) {
      history.push(routeConstants.private.paymentsOverview);
    }
  }, [history, activeTab, currentSiteId, currentSiteAcceptance]);

  useEffect(() => {
    if (activeTab === ActivePaymentsTab.REVIEW && (!currentSiteId || (currentSite && !reviewPermission))) {
      history.push(routeConstants.private.paymentsOverview);
    }
  }, [history, activeTab, currentSiteId, currentSite, reviewPermission]);

  const fetchPayments = (query: FetchDataQueryType) => {
    const site = query.meta as IEmployerSite;
    const siteId = site?.id;
    if (activeTab === ActivePaymentsTab.OVERVIEW && currentOrganization && siteId && sharedDates) {
      const { sort, offset, limit } = query;

      dispatch(
        getSitePaymentsRequest(siteId, {
          sort,
          offset,
          limit,
          q: [
            { key: 'payment_date', operator: ApiQueryOperator.gte, value: formatDateForApi(sharedDates[0]) },
            { key: 'payment_date', operator: ApiQueryOperator.lte, value: formatDateForApi(sharedDates[1]) },
          ],
        })
      );
    }
  };

  const accountBalance = useSelector(selectSiteAccountBalance());
  const acceptanceBalance = useSelector(selectSiteAcceptanceBalance());
  const activeBalance = activeTab === ActivePaymentsTab.ACCEPTANCE ? { value: acceptanceBalance, label: 'Acceptance' } : { value: accountBalance, label: 'Account' };

  return (
    <>
      <PageHeader>
        <PageLabel>Payments</PageLabel>

        <PageHeaderContent>
          <ConnectedSiteSelector appearance={SiteSelectorAppearance.PAGE_HEADER} />
          <PageActions display="column" align="end">
            <Label>{activeBalance.label} Balance</Label>
            <H2 data-cy="subtitle" color={activeBalance && activeBalance.value < 0 ? theme.color.danger.medium : 'inherit'}>
              {isLoading ? <Skeleton width={275} /> : activeBalance.value !== undefined ? centsToDollarsDisplay(activeBalance.value) : null}
            </H2>
          </PageActions>
        </PageHeaderContent>

        <TabNav>
          <TabNavItem data-cy="overview-nav-btn" label="Overview" isActive={activeTab === ActivePaymentsTab.OVERVIEW} to={routeConstants.private.paymentsOverview} />
          <TabNavItem data-cy="details-nav-btn" label="Details" isActive={activeTab === ActivePaymentsTab.DETAILS} to={routeConstants.private.paymentsDetail} />
          {currentSiteAcceptance?.enabled && (
            <TabNavItem data-cy="acceptance-nav-btn" label="Tip Acceptance" isActive={activeTab === ActivePaymentsTab.ACCEPTANCE} to={routeConstants.private.paymentsAcceptance} isUpdated={acceptancePendingTotal > 0} />
          )}
          {reviewPermission && <TabNavItem data-cy="review-nav-btn" label="Review" isActive={activeTab === ActivePaymentsTab.REVIEW} to={routeConstants.private.paymentsReview} isUpdated={reviewTotal > 0} />}
        </TabNav>
      </PageHeader>

      {activeTab === ActivePaymentsTab.OVERVIEW && currentOrganization && currentSite && (
        <ListContextProvider
          fetchData={fetchPayments}
          totalSelector={totalCountPaymentSetsSelector}
          {...(!data
            ? {
                defaultFilters: [
                  {
                    key: 'payment_date',
                    operator: ApiQueryOperator.gte,
                    value: formatDateForApi(sharedDates?.[0] || DEFAULT_START_DATE),
                  },
                  {
                    key: 'payment_date',
                    operator: ApiQueryOperator.lte,
                    value: formatDateForApi(sharedDates?.[1] || DEFAULT_END_DATE),
                  },
                ],
              }
            : {})}
          dependencies={currentSite}
          defaultSort={DEFAULT_SORT}
          persistURL={false}
          dataKey="paymentsOverviewPaymentSets"
        >
          <ListContext.Consumer>
            {({ methods, query }) => {
              const startDateFromUrl = (query?.q || []).find((item: any) => item.operator === ApiQueryOperator.gte);
              const endDateFromUrl = (query?.q || []).find((item: any) => item.operator === ApiQueryOperator.lte);

              return (
                <OverviewTab
                  isLoading={isLoading}
                  recordsPerPage={DEFAULT_PAGE_SIZE}
                  statsLoading={isStatsLoading}
                  paymentSets={paymentSets}
                  onNavigateToPaymentDetails={navigateToPaymentDetails}
                  paymentStats={paymentStatsByDate}
                  grossTotal={grossTotal}
                  holdbackTotal={holdbackTotal}
                  netTotal={netTotal}
                  instantTotal={instantTotal}
                  payrollTotal={payrollTotal}
                  paidCount={paidCount}
                  pendingCount={pendingCount}
                  canceledCount={canceledCount}
                  expiredCount={expiredCount}
                  paidTotal={paidTotal}
                  pendingTotal={pendingTotal}
                  canceledTotal={canceledTotal}
                  expiredTotal={expiredTotal}
                  businessDaySection={
                    <BusinessDaySection
                      isLoading={isLoading}
                      endDate={sharedDates?.[1] || endDateFromUrl.value || DEFAULT_END_DATE}
                      startDate={sharedDates?.[0] || startDateFromUrl.value || DEFAULT_START_DATE}
                      setDates={(dates) => {
                        setSharedDates(dates);
                        methods.onFiltersChange([
                          {
                            key: 'payment_date',
                            operator: ApiQueryOperator.gte,
                            value: formatDateForApi(dates[0]),
                          },
                          {
                            key: 'payment_date',
                            operator: ApiQueryOperator.lte,
                            value: formatDateForApi(dates[1]),
                          },
                        ]);
                      }}
                    />
                  }
                />
              );
            }}
          </ListContext.Consumer>
        </ListContextProvider>
      )}

      {activeTab === ActivePaymentsTab.DETAILS && currentOrganization && currentSite && <DetailsTab />}
      {activeTab === ActivePaymentsTab.ACCEPTANCE && currentOrganization && currentSite && <TipAcceptancePayments />}
      {activeTab === ActivePaymentsTab.REVIEW && currentOrganization && currentSite && <ReviewPayments />}
    </>
  );
};

export default PaymentsScreen;
